import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Hammer, ShoppingBag, MapPin, Eye, Heart, Clock } from 'lucide-react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { Auction } from '../types';
import { formatCurrency } from '../utils/formatting';
import { getImageUrl } from '../utils/imageUrl';
import { translateCategoryName } from '../utils/categoryI18n';
import { BRAND_LOGO_PUBLIC_PATH } from '../config/branding';
import LazyImage from './LazyImage';

interface AuctionCardProps {
  auction: Auction;
  onQuickBid?: (auction: Auction) => void;
}

const AuctionCard: React.FC<AuctionCardProps> = ({ auction, onQuickBid }) => {
  const { t } = useTranslation('common');
  const { currencySymbol } = useSiteSettings();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const isClassified = auction.type === 'CLASSIFIED';
  const isOwner = user && auction.sellerId && user.id === auction.sellerId;

  // Local state for favoriting
  const [isSavedLocal, setIsSavedLocal] = useState(auction.isFavorited || false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setIsSavedLocal(auction.isFavorited || false);
  }, [auction.isFavorited]);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error(t('card.loginToFavorite', 'يرجى تسجيل الدخول أولاً'));
      return;
    }

    if (isSaving) return;

    try {
      setIsSaving(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (isSavedLocal) {
        await axios.delete(apiUrl(`users/me/saved-auctions/${auction.id}`), { headers });
        setIsSavedLocal(false);
        toast.success(t('card.unfavorited', 'تم الإزالة من المفضلة'));
      } else {
        await axios.post(apiUrl(`users/me/saved-auctions/${auction.id}`), {}, { headers });
        setIsSavedLocal(true);
        toast.success(t('card.favorited', 'تمت الإضافة للمفضلة'));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(t('card.errorFavorite', 'حدث خطأ، يرجى المحاولة لاحقاً'));
    } finally {
      setIsSaving(false);
    }
  };

  // Time remaining calculation
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    if (isClassified || !auction.endTime) return 0;
    return new Date(auction.endTime).getTime() - Date.now();
  });

  useEffect(() => {
    if (isClassified || !auction.endTime) return;
    
    const updateRemaining = () => {
      const remaining = new Date(auction.endTime!).getTime() - Date.now();
      setTimeRemaining(remaining);
      return remaining;
    };

    const initialRemaining = updateRemaining();
    if (initialRemaining <= 0) return;

    const interval = setInterval(() => {
      const remaining = updateRemaining();
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [auction.endTime, isClassified]);

  const progressPercent = React.useMemo(() => {
    if (isClassified || !auction.endTime || !auction.startTime) return 0;
    const start = new Date(auction.startTime).getTime();
    const end = new Date(auction.endTime).getTime();
    const total = end - start;
    if (total <= 0) return 100;
    const passed = total - timeRemaining;
    const percent = (passed / total) * 100;
    return Math.min(Math.max(percent, 0), 100);
  }, [auction.startTime, auction.endTime, timeRemaining, isClassified]);

  const formatTimeRemaining = (ms: number) => {
    if (ms <= 0) return t('card.auctionEndedShort', 'Ended');
    const totalMinutes = Math.floor(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const seconds = Math.floor((ms % 60000) / 1000);
    
    const timeString = `${(hours % 24).toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${timeString} ${days} يوم`;
    }
    return timeString;
  };

  const displayImage = (auction.images && auction.images.length > 0) 
    ? getImageUrl(auction.images[0]) 
    : (auction.imageUrl ? getImageUrl(auction.imageUrl) : BRAND_LOGO_PUBLIC_PATH);

  const priceToDisplay = isClassified 
    ? (auction.fixedPrice || auction.startingPrice || auction.currentPrice || 0) 
    : (auction.currentPrice || auction.startingPrice || 0);

  return (
    <Link 
      to={`/auction/${auction.id}`}
      className={`relative rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-all duration-300 group flex flex-col h-full ${
        !isClassified && timeRemaining > 0 && timeRemaining <= 15000 
          ? 'bg-red-50 dark:bg-red-900/10 border-red-400 dark:border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.5)] animate-pulse' 
          : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800'
      }`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-[4/3] w-full bg-slate-100 dark:bg-slate-800 overflow-hidden shrink-0">
        <LazyImage
          src={displayImage}
          alt={auction.title}
          className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-300"
        />
        
        <div className="absolute top-2 right-2 z-10 flex gap-1">
          <span className={`${(isClassified || timeRemaining > 0) ? 'bg-emerald-500' : 'bg-slate-500'} text-white text-[10px] sm:text-xs font-bold px-2 py-0.5 sm:py-1 rounded shadow-sm`}>
            {isClassified ? 'فعال' : (timeRemaining > 0 ? 'فعال' : 'منتهي')}
          </span>
        </div>

        {/* Owner Badge */}
        {isOwner && (
          <div className="absolute top-2 left-2 z-10">
            <span className="bg-primary-600/90 text-white text-[10px] font-bold px-2 py-1 rounded shadow-sm">
              أنت المالك
            </span>
          </div>
        )}
        
        {/* Bottom Overlay Strip */}
        <div className="absolute bottom-0 left-0 right-0 bg-slate-900/70 backdrop-blur-sm text-white text-[10px] sm:text-[11px] font-medium px-2 py-1.5 flex justify-end items-center z-10">
          {/* Category */}
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="truncate max-w-[100px]">{translateCategoryName(auction.category, t)}</span>
            {isClassified ? <ShoppingBag className="w-3.5 h-3.5" /> : <Hammer className="w-3.5 h-3.5" />}
          </div>
        </div>

        {/* Fast Auction Progress Bar */}
        {!isClassified && timeRemaining > 0 && (
          <div className={`absolute bottom-0 left-0 right-0 h-2 bg-slate-200/20 z-20 ${timeRemaining <= 15000 ? 'overflow-visible' : 'overflow-hidden'}`}>
            <div 
              className={`h-full transition-all duration-1000 ease-linear animate-stripes ${
                timeRemaining <= 15000 
                  ? 'bg-red-500 animate-pulse shadow-[0_0_10px_rgba(239,68,68,0.8)]' 
                  : (100 - progressPercent) > 50 ? 'bg-emerald-500' : 'bg-orange-500'
              }`}
              style={{ 
                width: `${Math.max(100 - progressPercent, 0)}%`,
                backgroundImage: 'linear-gradient(45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent)',
                backgroundSize: '1rem 1rem'
              }}
            />
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-2.5 sm:p-3 flex flex-col justify-between flex-grow gap-2 sm:gap-3 bg-white dark:bg-slate-900">
        
        <div className="flex flex-col gap-1.5 sm:gap-2">
          {/* Title */}
          <h3 
            className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100 line-clamp-2 leading-snug"
            title={auction.title}
          >
            {auction.title}
          </h3>

          {/* Time remaining */}
          {!isClassified && (
            <div className={`flex items-center gap-1.5 text-[10px] sm:text-xs font-bold px-2 py-1 rounded w-fit transition-all duration-300 ${
              timeRemaining > 0 && timeRemaining <= 90000 
                ? 'bg-red-50 text-red-600 dark:bg-red-900/40 dark:text-red-400 border border-red-200 dark:border-red-800 shadow-[0_0_12px_rgba(239,68,68,0.6)] animate-pulse' 
                : 'text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800'
            }`}>
              <Clock className={`w-3.5 h-3.5 ${timeRemaining > 0 && timeRemaining <= 90000 ? 'text-red-500 animate-bounce' : 'text-orange-500'}`} />
              <span dir="ltr">{formatTimeRemaining(timeRemaining)}</span>
            </div>
          )}

          {/* Views & Bids row */}
          <div className="flex items-center gap-4 text-[10px] sm:text-xs text-slate-500 font-medium">
            {!isClassified && (
              <div className="flex items-center gap-1">
                <span>{auction.bidCount || 0} مزايدة</span>
                <Hammer className="w-3.5 h-3.5" />
              </div>
            )}
            <div className="flex items-center gap-1">
              <span>{auction.viewCount || 0} مشاهدة</span>
              <Eye className="w-3.5 h-3.5" />
            </div>
          </div>

          {/* Location row */}
          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-orange-600 font-bold">
            <span className="truncate">{auction.realEstateDetails?.city || auction.country || 'غير محدد'}</span>
            <MapPin className="w-3.5 h-3.5" />
          </div>
        </div>

        {/* Footer row: Price & Action */}
        <div className="mt-2 sm:mt-auto pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
           <div className="flex items-center gap-1.5 sm:gap-2">
             <button 
               className="bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-[10px] sm:text-xs font-bold px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-slate-800 dark:hover:bg-white transition-colors"
               onClick={(e) => {
                 if (isOwner || isClassified || (!isClassified && timeRemaining <= 0)) {
                   return; // Natural link click
                 }
                 if (!user) {
                   e.preventDefault();
                   e.stopPropagation();
                   toast.error(t('card.toastLoginBid', 'يجب تسجيل الدخول للمزايدة'));
                   navigate('/login');
                   return;
                 }
                 if (onQuickBid) {
                   e.preventDefault();
                   e.stopPropagation();
                   onQuickBid(auction);
                 }
               }}
             >
               {(!isClassified && timeRemaining <= 0) ? (isOwner ? 'تواصل مع المشتري' : 'تواصل مع البائع') : (isOwner || isClassified ? t('card.viewDetails', 'التفاصيل') : 'قدم مزايدة')}
             </button>
             {user && (
                <button 
                  className={`p-1 sm:p-1.5 border rounded-md transition-all duration-300 ${isSavedLocal ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-500' : 'border-slate-200 dark:border-slate-700 text-slate-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20'}`}
                  onClick={handleFavoriteClick}
                  disabled={isSaving}
                  title="إضافة للمفضلة"
                >
                  <Heart className={`w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform ${isSavedLocal ? 'fill-current scale-110' : ''} ${isSaving ? 'animate-pulse' : 'hover:scale-110'}`} />
                </button>
              )}
           </div>

          <div className="flex flex-col items-end">
             {isClassified ? (
               <span className="text-[9px] sm:text-[10px] text-slate-500 font-medium">السعر المطلوب</span>
             ) : (
               <span className={`text-[9px] sm:text-[10px] font-medium ${(auction.bidCount || 0) > 0 ? 'text-emerald-600 dark:text-emerald-400 font-bold' : 'text-slate-500'}`}>
                 {(auction.bidCount || 0) > 0 ? 'آخر مزايدة' : 'السعر الافتتاحي'}
               </span>
             )}
             {(String(auction.category) === 'donations' || String(auction.category).toLowerCase() === 'donations') && priceToDisplay === 0 ? (
               <span className="font-extrabold text-green-600 text-xs sm:text-sm bg-green-100 dark:bg-green-900/40 px-2 py-0.5 rounded">
                 مجاني / تبرع
               </span>
             ) : (
               <span className="font-bold text-orange-600 text-xs sm:text-sm">
                 {formatCurrency(priceToDisplay, currencySymbol)}
               </span>
             )}
          </div>

        </div>
      </div>

      {/* Progress Bar for Auctions */}
      {!isClassified && auction.endTime && timeRemaining > 0 && (
        <div className="h-1 w-full bg-slate-200 dark:bg-slate-700 shrink-0">
          <div 
            className="h-full bg-orange-500 transition-all duration-1000 ease-linear"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      )}
    </Link>
  );
};

export default AuctionCard;
